import {
  type Filter,
  FilterId,
  type FilterSelectionsMap,
  FilterType,
  isMultiFilter,
} from '../types/filters';
import { formatFilterDisplay } from '../utils/formatFilterValue';

const IGNORED_FILTER_IDS = new Set<FilterId>([
  FilterId.ROLE_TAXONOMY,
  FilterId.SKILL_TAXONOMY,
  FilterId.DATA_ADJUSTMENTS,
  FilterId.CONTINGENT_WORKERS,
]);

const isContextEligibleFilter = (filter: Filter): boolean => {
  if (IGNORED_FILTER_IDS.has(filter.id)) {
    return false;
  }

  if (isMultiFilter(filter)) {
    return filter.instances.some(
      (instance) => instance.isUserSubmitted === true
    );
  }

  return filter.isUserSubmitted === true;
};

const joinNames = (values: string[]): string =>
  values.filter(Boolean).join(', ');

const formatContextValue = (filter: Filter): string => {
  if (isMultiFilter(filter)) {
    const instances = filter.instances.filter(
      (instance) => instance.isUserSubmitted === true
    );
    return joinNames(instances.map((instance) => formatContextValue(instance)));
  }

  switch (filter.type) {
    case FilterType.LIST:
    case FilterType.STATIC_LIST:
      return joinNames(filter.selections.map((selection) => selection.name));
    case FilterType.TREE: {
      const selections =
        filter.selections.length > 0
          ? filter.selections
          : (filter.primarySelections?.filter(
              (selection) => selection.isActive
            ) ?? []);
      return joinNames(selections.map((selection) => selection.name));
    }
    case FilterType.FREE_TEXT:
      return joinNames(filter.text);
    case FilterType.SCHOOL:
      return joinNames(
        Object.values(filter.schoolResultItems).map((school) => school.name)
      );
    default:
      return formatFilterDisplay(filter);
  }
};

const getContextLabel = (filter: Filter): string => {
  if (isMultiFilter(filter)) {
    return filter.instances[0]?.name ?? filter.name;
  }

  return filter.name;
};

export const buildAiSearchCurrentFilterContext = (
  filterMap: FilterSelectionsMap
): string | undefined => {
  const lines = Array.from(filterMap.values())
    .filter(isContextEligibleFilter)
    .map((filter) => {
      const value = formatContextValue(filter);
      return value ? `${getContextLabel(filter)}: ${value}` : null;
    })
    .filter((line): line is string => line !== null);

  return lines.length > 0 ? lines.join('\n') : undefined;
};
